import React, { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from './supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  /**
   * Board Member: a full admin whose UI hides ActBlue functions (import,
   * reconciliation, the dashboard webhook alert). Subtractive flag on top of
   * isAdmin — the DB/RLS layer still treats them as admin.
   */
  isBoardMember: boolean;
  /**
   * True when the user may see/use ActBlue functions: an admin who is NOT a
   * board member. Use this to gate every ActBlue surface.
   */
  canManageActBlue: boolean;
  adminChecked: boolean;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

async function checkAdminStatus(user: User | null): Promise<boolean> {
  if (!user) return false;
  try {
    const { data, error } = await supabase.rpc('is_admin', { user_id: user.id });
    if (error) {
      console.error('Error checking admin status', error);
      return false;
    }
    return Boolean(data);
  } catch (error) {
    console.error('Unexpected error checking admin status', error);
    return false;
  }
}

async function checkBoardMemberStatus(user: User | null): Promise<boolean> {
  if (!user) return false;
  try {
    const { data, error } = await supabase.rpc('is_board_member', { user_id: user.id });
    if (error) {
      console.error('Error checking board-member status', error);
      return false;
    }
    return Boolean(data);
  } catch (error) {
    console.error('Unexpected error checking board-member status', error);
    return false;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBoardMember, setIsBoardMember] = useState(false);
  const [adminChecked, setAdminChecked] = useState(false);
  const adminCheckToken = useRef(0);

  const evaluateUser = useCallback(async (nextUser: User | null) => {
    adminCheckToken.current += 1;
    const currentToken = adminCheckToken.current;
    setUser(nextUser);
    setLoading(false);
    setAdminChecked(false);
    // Fetch admin + board-member status together.
    const [admin, board] = await Promise.all([
      checkAdminStatus(nextUser),
      checkBoardMemberStatus(nextUser),
    ]);
    if (currentToken !== adminCheckToken.current) {
      return;
    }
    setIsAdmin(admin);
    setIsBoardMember(board);
    setAdminChecked(true);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      evaluateUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Fire-and-forget: link auth UUID → members.user_id on first login.
        // Non-blocking — login proceeds regardless of outcome.
        //
        // No body is sent: the function derives the caller's identity from the
        // session JWT that supabase-js attaches automatically. Sending id/email
        // here would be ignored server-side (and must never be trusted).
        supabase.functions.invoke('auth-link').catch(() => {
          // Intentionally silent — never block login on a link failure
        });
      }
      evaluateUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, [evaluateUser]);

  const signOut = async () => {
    await supabase.auth.signOut();
    await evaluateUser(null);
  };

  const refreshSession = async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    await evaluateUser(session?.user ?? null);
  };

  const value = useMemo(() => ({
    user,
    loading,
    isAdmin,
    isBoardMember,
    // ActBlue is allowed for admins who are NOT board members.
    canManageActBlue: isAdmin && !isBoardMember,
    adminChecked,
    signOut,
    refreshSession,
  }), [user, loading, isAdmin, isBoardMember, adminChecked]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
